function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { t, validateNonEmpty, validateInteger, getNumberFormatter } from '@superset-ui/core';
import { D3_FORMAT_OPTIONS, D3_FORMAT_DOCS, getStandardizedControls } from '@superset-ui/chart-controls';
import { countryOptions } from '../countries';
var numberFormatPreviewValue = 12345.432;
var currencyFormatIndex = 0;
for (var i = 0; i < D3_FORMAT_OPTIONS.length; i += 1) {
  currencyFormatIndex = i;
  if (D3_FORMAT_OPTIONS[i][0] === '$,.2f') {
    break;
  }
}
var D3_MAP_FORMAT_OPTIONS = Array.prototype.concat(D3_FORMAT_OPTIONS.toSpliced(currencyFormatIndex + 1, 0, ['$,d', '$,d ' + (numberFormatPreviewValue + " => " + getNumberFormatter('$,d')(numberFormatPreviewValue))]));
var D3_LEGEND_FORMAT_OPTIONS = Array.prototype.concat([['AS_MAP', t('Same as map')]], D3_FORMAT_OPTIONS);
var config = {
  controlPanelSections: [{
    label: t('Query'),
    expanded: true,
    controlSetRows: [[{
      name: 'select_country',
      config: {
        type: 'SelectControl',
        label: t('Country'),
        default: null,
        choices: countryOptions,
        description: t('Which country to plot the map for?'),
        validators: [validateNonEmpty]
      }
    }], ['entity'], ['metric'], ['adhoc_filters']]
  }, {
    label: t('Chart Options'),
    expanded: true,
    tabOverride: 'customize',
    controlSetRows: [[{
      name: 'number_format',
      config: {
        type: 'SelectControl',
        freeForm: true,
        label: t('Number format'),
        renderTrigger: true,
        default: 'SMART_NUMBER',
        choices: D3_MAP_FORMAT_OPTIONS,
        description: D3_FORMAT_DOCS
      }
    }], ['linear_color_scheme']]
  }, {
    label: t('Legend Options'),
    expanded: true,
    tabOverride: 'customize',
    controlSetRows: [[{
      name: 'number_format_legend',
      config: {
        type: 'SelectControl',
        freeForm: true,
        label: t('Number format legend'),
        renderTrigger: true,
        default: 'AS_MAP',
        choices: D3_LEGEND_FORMAT_OPTIONS,
        description: D3_FORMAT_DOCS
      }
    }], [{
      name: 'legend_no_cells',
      config: {
        type: 'SelectControl',
        clearable: false,
        freeForm: true,
        label: t('Legend cells'),
        choices: [[2, '2'], [3, '3'], [5, '5'], [9, '9'], [10, '10']],
        default: 10,
        description: t('Number of cells in legend'),
        validators: [validateNonEmpty, validateInteger],
        renderTrigger: true
      }
    }], [{
      name: 'legend_position',
      config: {
        type: 'SelectControl',
        clearable: false,
        freeForm: false,
        label: t('Legend position'),
        choices: [['left', t('Left')], ['right', t('Right')], ['top', t('Top')], ['bottom', t('Bottom')]],
        default: 'left',
        description: t('Where the legend should be positioned relative to map.'),
        renderTrigger: true,
        validators: [validateNonEmpty]
      }
    }], [{
      name: 'legend_distribution',
      config: {
        type: 'SelectControl',
        clearable: false,
        freeForm: false,
        label: t('Legend distribution'),
        choices: [['linear', t('Linear')], ['mid80', t('Mid 80%')]],
        default: 'linear',
        description: t('Influences which points will make it into legend'),
        renderTrigger: true,
        validators: [validateNonEmpty]
      }
    }], [{
      name: 'legend_settable_zero',
      config: {
        type: 'SelectControl',
        clearable: false,
        freeForm: true,
        label: t('Settable zero'),
        choices: [['average', t('Average (default)')], ['median', t('Median')], ['0', '0']],
        default: 'average',
        description: t('Influences which value will be in the centerpoint of color pallete'),
        renderTrigger: true,
        validators: [validateNonEmpty]
      }
    }], [{
      name: 'legend_zero_symmetricity',
      config: {
        type: 'SelectControl',
        clearable: false,
        freeForm: false,
        label: t('Legend symmetricity'),
        choices: [['symmetric', t('Symmetric')], ['asymmetric', t('Asymmetric')]],
        default: 'symmetric',
        description: t('Legend symmetricity (when zero is set)'),
        renderTrigger: true,
        validators: []
      }
    }]]
  }],
  controlOverrides: {
    entity: {
      label: t('ISO 3166-2 Codes'),
      description: t('Column containing ISO 3166-2 codes of region/province/department in your table.')
    },
    metric: {
      label: t('Metric'),
      description: t('Metric to display bottom title')
    },
    linear_color_scheme: {
      renderTrigger: true
    }
  },
  formDataOverrides: formData => _extends({}, formData, {
    entity: getStandardizedControls().shiftColumn(),
    metric: getStandardizedControls().shiftMetric()
  })
};
export default config;